import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  ArrowLeft, Database, Download, Trash2, RefreshCw, Clock,
  CheckCircle, XCircle, Loader2, AlertTriangle, Calendar, RotateCcw, Upload
} from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminBackups() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [nextBackup, setNextBackup] = useState(null);
  const [backupSchedule, setBackupSchedule] = useState("");
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      const response = await fetch(`${API}/admin/backups`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error("Failed to fetch backups");

      const data = await response.json();
      setBackups(data.backups || []);
      setNextBackup(data.next_scheduled_backup);
      setBackupSchedule(data.backup_schedule);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API}/admin/backups/create`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error("Failed to create backup");

      const data = await response.json();
      toast.success(`Backup created: ${data.total_records} records backed up`);
      fetchBackups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async () => {
    if (!selectedBackup) return;
    
    setRestoring(true);
    try {
      const response = await fetch(`${API}/admin/backups/${selectedBackup.backup_id}/restore`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error("Failed to restore backup");

      const data = await response.json();
      toast.success(`Backup restored successfully! Safety backup created: ${data.safety_backup_id}`);
      setShowRestoreDialog(false);
      fetchBackups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRestoring(false);
    }
  };

  const deleteBackup = async () => {
    if (!selectedBackup) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/admin/backups/${selectedBackup.backup_id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error("Failed to delete backup");

      toast.success("Backup deleted");
      setShowDeleteDialog(false);
      fetchBackups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const downloadBackup = (backupId) => {
    window.open(`${API}/admin/backups/${backupId}/download`, "_blank");
  };

  const uploadRestore = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);

      const headers = {};
      const storedToken = localStorage.getItem('session_token');
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;

      const response = await fetch(`${API}/admin/backups/upload-restore`, {
        method: "POST",
        headers,
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to restore from uploaded backup");
      }

      const data = await response.json();
      toast.success(`Restored ${data.total_records_restored.toLocaleString()} records from uploaded backup. Safety backup: ${data.safety_backup_id}`);
      setShowUploadDialog(false);
      setUploadFile(null);
      fetchBackups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>;
      case "failed":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
      case "in_progress":
        return <Badge className="bg-yellow-600"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> In Progress</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "scheduled":
        return <Badge variant="outline" className="border-blue-500 text-blue-400"><Clock className="w-3 h-3 mr-1" /> Scheduled</Badge>;
      case "manual":
        return <Badge variant="outline" className="border-green-500 text-green-400"><RefreshCw className="w-3 h-3 mr-1" /> Manual</Badge>;
      case "pre_restore_safety":
        return <Badge variant="outline" className="border-yellow-500 text-yellow-400"><AlertTriangle className="w-3 h-3 mr-1" /> Safety</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Database className="w-6 h-6 text-primary" />
              <h1 className="font-heading text-xl font-bold uppercase">Backup Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUploadDialog(true)}
              data-testid="upload-restore-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Upload & Restore
            </Button>
            <Button onClick={createBackup} disabled={creating} data-testid="create-backup-btn">
              {creating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
              ) : (
                <><Database className="w-4 h-4 mr-2" /> Create Backup Now</>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* Schedule Info Card */}
        <Card className="mb-6 border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Backup Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Schedule</p>
                <p className="text-lg font-medium">{backupSchedule}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Next Scheduled Backup</p>
                <p className="text-lg font-medium">{formatDate(nextBackup)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backups List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              Available Backups ({backups.length})
            </CardTitle>
            <CardDescription>
              Backups are automatically retained for 12 weeks (approximately 3 months)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {backups.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No backups available</p>
                <p className="text-sm">Create your first backup to protect your data</p>
              </div>
            ) : (
              <div className="space-y-4">
                {backups.map((backup) => (
                  <div
                    key={backup.backup_id}
                    className="p-4 border border-border rounded-lg bg-secondary/10 hover:border-primary/50 transition-colors"
                    data-testid={`backup-${backup.backup_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(backup.status)}
                          {getTypeBadge(backup.backup_type)}
                        </div>
                        <p className="font-mono text-sm text-muted-foreground mb-1">
                          ID: {backup.backup_id}
                        </p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-muted-foreground">
                            <Clock className="w-4 h-4 inline mr-1" />
                            {formatDate(backup.timestamp)}
                          </span>
                          <span className="text-muted-foreground">
                            <Database className="w-4 h-4 inline mr-1" />
                            {backup.total_records?.toLocaleString() || 0} records
                          </span>
                        </div>
                        {backup.error && (
                          <p className="text-red-400 text-sm mt-2">
                            <AlertTriangle className="w-4 h-4 inline mr-1" />
                            {backup.error}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadBackup(backup.backup_id)}
                          disabled={backup.status !== "completed"}
                        >
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedBackup(backup);
                            setShowRestoreDialog(true);
                          }}
                          disabled={backup.status !== "completed"}
                          className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Restore
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedBackup(backup);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Restore Confirmation Dialog */}
        <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-yellow-400">
                <AlertTriangle className="w-5 h-5" />
                Confirm Restore
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to restore from this backup? This will:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Create a safety backup of current data first</li>
                  <li>Replace all current data with backup data</li>
                  <li>This action affects all users and cannot be easily undone</li>
                </ul>
              </DialogDescription>
            </DialogHeader>
            {selectedBackup && (
              <div className="p-3 bg-secondary/20 rounded border border-border">
                <p className="text-sm"><strong>Backup ID:</strong> {selectedBackup.backup_id}</p>
                <p className="text-sm"><strong>Created:</strong> {formatDate(selectedBackup.timestamp)}</p>
                <p className="text-sm"><strong>Records:</strong> {selectedBackup.total_records?.toLocaleString()}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={restoreBackup}
                disabled={restoring}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {restoring ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...</>
                ) : (
                  <><RotateCcw className="w-4 h-4 mr-2" /> Restore Backup</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400">
                <Trash2 className="w-5 h-5" />
                Delete Backup
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this backup? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {selectedBackup && (
              <div className="p-3 bg-secondary/20 rounded border border-border">
                <p className="text-sm"><strong>Backup ID:</strong> {selectedBackup.backup_id}</p>
                <p className="text-sm"><strong>Created:</strong> {formatDate(selectedBackup.timestamp)}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={deleteBackup}
                disabled={deleting}
              >
                {deleting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" /> Delete Backup</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Upload & Restore Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={(open) => { setShowUploadDialog(open); if (!open) setUploadFile(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-primary">
                <Upload className="w-5 h-5" />
                Upload & Restore Backup
              </DialogTitle>
              <DialogDescription>
                Upload a previously downloaded TOOLIO backup file (.json) to restore your data. This will:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Create a safety backup of current data first</li>
                  <li>Replace all current data with the uploaded backup</li>
                  <li>This action affects all users and cannot be easily undone</li>
                </ul>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="hidden"
                  id="backup-file-input"
                  data-testid="backup-file-input"
                />
                <label htmlFor="backup-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  {uploadFile ? (
                    <div>
                      <p className="font-medium text-foreground">{uploadFile.name}</p>
                      <p className="text-sm text-muted-foreground">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-muted-foreground">Click to select a backup file</p>
                      <p className="text-xs text-muted-foreground mt-1">JSON files only (.json)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadFile(null); }}>
                Cancel
              </Button>
              <Button
                onClick={uploadRestore}
                disabled={uploading || !uploadFile}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...</>
                ) : (
                  <><RotateCcw className="w-4 h-4 mr-2" /> Restore from File</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
